import { FormikErrors, FormikValues } from 'formik';
import { GenericObject, formatDate, formatPhoneValue, getApplicationId, getIsPortal } from '@fnol/library/src/lib';
import { constValues, insuranceGroup, legalPersonType, objectType, policiesProperties } from './consts';

export const prepareCitiesList = (cities: { id: number; naziv: string; sifra: string; postanskiBroj: string }[]) => {
    return cities.map((city) => {
        return {
            id: city.id,
            value: `${city.id}/${city.naziv}`,
            label: city.naziv,
            zipCode: city.postanskiBroj,
            code: city.sifra,
        };
    });
};

export const preparePublicUtilitiesList = (publicUtilities: { id: number; naziv: string }[] = []) => {
    return publicUtilities.map((publicUtility) => {
        return {
            id: publicUtility.id,
            value: publicUtility.naziv,
        };
    });
};

export const prepareUnionsList = (unions: { id: number; naziv: string }[] = []) => {
    return unions.map((union) => {
        return {
            id: union.id,
            value: union.naziv,
        };
    });
};

export const prepareStreetNames = (streetNames: { sifra: number; naziv: string }[] = []) => {
    return streetNames.map((streetName) => {
        return {
            id: streetName.sifra.toString(),
            value: streetName.naziv,
            label: streetName.naziv,
        };
    });
};

export const prepareDamageTypes = (
    damageTypes: {
        id: number;
        naziv: string;
        opis: string;
        obavezanBrojOstecenihProstorija: boolean;
        vrstaObjektaId: number;
        uslovZaPrikazivanjeSpecifikacije: boolean;
    }[] = [],
) => {
    return damageTypes.map((damageType) => {
        return {
            id: damageType.id,
            primary: damageType.naziv,
            secondary: damageType.opis,
            checked: false,
            shouldShowDamagedRooms: damageType.obavezanBrojOstecenihProstorija,
            objectTypeId: damageType.vrstaObjektaId,
            shouldShowSpcification: damageType.uslovZaPrikazivanjeSpecifikacije,
        };
    });
};

export const preparePersonRoles = (
    personRoles: {
        id: number;
        naziv: string;
        prikazFL: boolean;
        prikazPL: boolean;
        unosVlasnika: boolean;
    }[],
    personType: legalPersonType,
) => {
    return personRoles
        .filter((personRole) => {
            if (personType === legalPersonType.individual) {
                return personRole.prikazFL === true;
            } else if (personType === legalPersonType.legal) {
                return personRole.prikazPL === true;
            }
        })
        .map((personRole) => {
            return {
                id: personRole.id,
                value: personRole.id,
                text: personRole.naziv,
            };
        });
};
export const prepareInsuredPerson = (insuredPerson: {
    adresa: string;
    broj: string;
    drzavaId: number;
    drzavaNaziv: string;
    email: string;
    ime: string;
    jmbg: string;
    mestoId: number;
    mestoNaziv: string;
    mestoSifra: string;
    prezime: string;
    ptt: string;
    telefon: string;
}) => {
    return {
        address: insuredPerson.adresa,
        addressNumber: insuredPerson.broj,
        countryId: insuredPerson.drzavaId.toString(10),
        countryName: insuredPerson.drzavaNaziv,
        email: insuredPerson.email,
        name: insuredPerson.ime,
        lastName: insuredPerson.prezime,
        ucn: insuredPerson.jmbg,
        cityId: insuredPerson.mestoId,
        cityName: insuredPerson.mestoNaziv,
        cityCode: insuredPerson.mestoSifra,
        postalCode: insuredPerson.ptt,
        phone: insuredPerson.telefon,
    };
};

export const showOwnerSelector = (
    personRoles: {
        id: number;
        naziv: string;
        prikazFL: boolean;
        prikazPL: boolean;
        unosVlasnika: boolean;
    }[],
    value: number,
) => {
    return personRoles
        .filter((personRole) => {
            return personRole.id === value;
        })
        .map((personRole) => {
            return personRole.unosVlasnika;
        });
};

export const preparePersonValidationData = (
    personValidation: {
        naziv: string;
        poruka: string;
    }[],
    value: string,
) => {
    return personValidation
        .filter((personValidation) => {
            return personValidation.naziv === value;
        })
        .map((personValidation) => {
            return personValidation.poruka;
        });
};

/**
 * Prepare data for causes dropdown list
 *
 * @param causes - list from API endpoint
 * @returns list of causes in format for dropdown list
 */
export const prepareCausesList = (causes: { id: number; naziv: string; kategorija: string }[]) => {
    return causes.map((cause) => {
        return {
            id: cause.id,
            text: cause.naziv,
            category: cause.kategorija,
            value: cause.id,
        };
    });
};

export const scrollToFirstError = (errors: FormikErrors<GenericObject>) => {
    const errorKeys = Object.keys(errors);

    if (errorKeys.length > 0) {
        const firstElement = document.querySelector(`[name=${errorKeys[0]}]`) as HTMLElement;

        const getTopOffset = (el: HTMLElement): number => {
            const labelOffset = 150;
            return el?.getBoundingClientRect().top + window.scrollY - labelOffset;
        };

        window.scroll({
            top: getTopOffset(firstElement),
            left: 0,
            behavior: 'smooth',
        });
    }
};

/**
 * Prepare Formik data for submit
 *
 * @param values - object containing Formik state
 * @returns dataForSubmit - object with mapped data for submit
 */

export const prepareDataForSubmit = (values: FormikValues) => {
    const dataForSubmit: GenericObject = {};
    dataForSubmit.brojPolise = values.policyNumberAPI; // policy number returned by API can be different than search criteria
    dataForSubmit.datumNastanka = formatDate(values.dateOfOccurance);
    dataForSubmit.vrstaObjektaStetaId = values.damagedObject;
    dataForSubmit.podnosilacVrstaLica = values.submitterType;

    if (getIsPortal(getApplicationId())) {
        dataForSubmit.nacinOsiguranjaID = values.activePolicy[policiesProperties.insuranceMethodIDFromBE];
        dataForSubmit.brPolisePotvrde = values.policyNumberAPI;
    } else {
        dataForSubmit.nacinOsiguranjaID = values.insuranceMethod;

        if (
            values.groupInsuranceMethod === insuranceGroup.policyNumber ||
            values.groupInsuranceMethod === insuranceGroup.policyNumberBasic
        ) {
            dataForSubmit.brPolisePotvrde = values.policyNumber;
        }
        if (values.groupInsuranceMethod === insuranceGroup.mobileOrWebApp) {
            dataForSubmit.brPolisePotvrde = values.insuranceConfirmationID;
        }
        if (values.groupInsuranceMethod === insuranceGroup.directMail) {
            dataForSubmit.pozivNaBrDirektnaPosta = values.referenceNumber;
        }
        if (values.groupInsuranceMethod === insuranceGroup.union) {
            dataForSubmit.sindikatID = values.union;
        }
        if (values.groupInsuranceMethod === insuranceGroup.publicUtility) {
            dataForSubmit.jkpID = values.publicUtility;
        }
    }

    let personRole = 0;

    if (values.submitterType === legalPersonType.individual) {
        dataForSubmit.podnosilacFL = {
            ime: values.submitterIndividualName,
            prezime: values.submitterIndividualLastName,
            drzavaId: values.submitterIndividualCountry,
            jmbg:
                values.submitterIndividualCountry == constValues.countrySerbiaId
                    ? values.submitterIndividualUCN
                    : '1111111111111',
            adresa: values.submitterIndividualAddress.label
                ? values.submitterIndividualAddress.label
                : values.submitterIndividualAddress,
            adresaBroj: values.submitterIndividualAddressNumber,
            adresaStan: values.submitterIndividualAddressApartment,
            datumRodjenja: formatDate(values.submitterIndividualDateOfBirth),
            mobilniTelefon: formatPhoneValue(values.submitterIndividualPhone),
            email: values.submitterIndividualEmail,
            svojstvoLicaId: values.submitterIndividualPersonRole,
        };
        if (
            values.submitterIndividualCountry !== constValues.countrySerbiaId.toString(10) &&
            values.submitterIndividualCountry !== constValues.countrySerbiaId
        ) {
            dataForSubmit.podnosilacFL.mesto = values.submitterIndividualCity;
            dataForSubmit.podnosilacFL.brojPasosa = values.submitterIndividualPassportNumber;
        } else {
            dataForSubmit.podnosilacFL.mestoId = values.submitterIndividualCityInSerbia.id;
            dataForSubmit.podnosilacFL.mesto = values.submitterIndividualCityInSerbia.label;
            dataForSubmit.podnosilacFL.brojPasosa = null;
        }
        if (getIsPortal(getApplicationId())) {
            dataForSubmit.podnosilacFL.tekuciRacun = values.submitterIndividualAccountNumber;
        }
        personRole = values.submitterIndividualPersonRole;
    }
    if (values.submitterType === legalPersonType.legal) {
        dataForSubmit.podnosilacPL = {
            naziv: values.submitterLegalEntityName,
            drzavaId: constValues.countrySerbiaId,
            mestoId: values.submitterLegalEntityCityInSerbia.id,
            mesto: values.submitterLegalEntityCityInSerbia.label,
            adresa: values.submitterLegalEntityAddress.value
                ? values.submitterLegalEntityAddress.value
                : values.submitterLegalEntityAddress,
            adresaBroj: values.submitterLegalEntityAddressNumber,
            adresaStan: values.submitterLegalEntityAddressApartment,
            maticniBroj: values.submitterLegalEntityID,
            pib: values.submitterLegalEntityVAT,
            email: values.submitterLegalEntityEmail,
            telefon: formatPhoneValue(values.submitterLegalEntityPhone),
            svojstvoLicaId: values.submitterLegalEntityPersonRole,
            kontaktIme: values.submitterLegalEntityContactName,
            kontaktPrezime: values.submitterLegalEntityContactLastName,
            kontaktMobilniTelefon: formatPhoneValue(values.submitterLegalEntityContactPhone),
            kontaktEmail: values.submitterLegalEntityContactEmail,
        };
        personRole = values.submitterLegalEntityPersonRole;
    }

    if (
        getIsPortal(getApplicationId()) ||
        (values.groupInsuranceMethod === insuranceGroup.policyNumber && values.damagedObject == objectType.insured)
    ) {
        dataForSubmit.objekatAdresa = values.insuredAddress;
        dataForSubmit.objekatMesto = values.insuredCity;
    }

    if (!getIsPortal(getApplicationId())) {
        if (
            values.groupInsuranceMethod !== insuranceGroup.policyNumber ||
            (values.groupInsuranceMethod === insuranceGroup.policyNumber && values.damagedObject == objectType.other)
        ) {
            if (values.addressObjectSameAsSubmitter === 'false') {
                dataForSubmit.objekatMesto = values.objectCity.label;
                dataForSubmit.objekatAdresa = values.objectStreet.value
                    ? `${values.objectStreet.label} ${values.objectStreetNumber}`
                    : values.objectStreet;
            }
            if (values.addressObjectSameAsSubmitter === 'true') {
                if (values.submitterType === legalPersonType.individual) {
                    dataForSubmit.objekatAdresa = values.submitterIndividualAddress.value
                        ? `${values.submitterIndividualAddress.label} ${values.submitterIndividualAddressNumber}`
                        : values.submitterIndividualAddress;
                    if (
                        values.submitterIndividualCountry === constValues.countrySerbiaId.toString(10) ||
                        values.submitterIndividualCountry === constValues.countrySerbiaId
                    )
                        dataForSubmit.objekatMesto = values.submitterIndividualCityInSerbia.label;
                    else dataForSubmit.objekatMesto = values.submitterIndividualCity;
                }
                if (values.submitterType === legalPersonType.legal) {
                    dataForSubmit.objekatAdresa = `${values.submitterLegalEntityAddress.label} ${values.submitterLegalEntityAddressNumber}`;
                    if (
                        values.submitterLegalEntityCountry === constValues.countrySerbiaId.toString(10) ||
                        values.submitterLegalEntityCountry === constValues.countrySerbiaId
                    )
                        dataForSubmit.objekatMesto = values.submitterLegalEntityCityInSerbia.label;
                    else dataForSubmit.objekatMesto = values.submitterLegalEntityCity;
                }
            }
        }
    }

    if (
        values.groupInsuranceMethod === insuranceGroup.policyNumber ||
        (values.groupInsuranceMethod !== insuranceGroup.policyNumber && values.damagedObject === objectType.insured)
    )
        dataForSubmit.vlasnikPodnosilac = !showOwnerSelector(values.personRoles, personRole)[0];
    else dataForSubmit.vlasnikPodnosilac = false;

    if (!dataForSubmit.vlasnikPodnosilac) {
        if (values.ownerType) dataForSubmit.vlasnikVrstaLica = values.ownerType;

        if (values.ownerType === legalPersonType.individual) {
            dataForSubmit.vlasnikFL = {
                ime: values.ownerIndividualName,
                prezime: values.ownerIndividualLastName,
                drzavaId: values.ownerIndividualCountry,
                adresa: values.ownerIndividualAddress.value
                    ? values.ownerIndividualAddress.value
                    : values.ownerIndividualAddress,
                adresaBroj: values.ownerIndividualAddressNumber,
                adresaStan: values.ownerIndividualAddressApartment,
                datumRodjenja: formatDate(values.ownerIndividualDateOfBirth),
                jmbg:
                    values.ownerIndividualCountry == constValues.countrySerbiaId
                        ? values.ownerIndividualUCN
                        : '1111111111111',
                mobilniTelefon: formatPhoneValue(values.ownerIndividualPhone),
                email: values.ownerIndividualEmail,
            };
            if (
                values.ownerIndividualCountry !== constValues.countrySerbiaId.toString(10) &&
                values.ownerIndividualCountry !== constValues.countrySerbiaId
            ) {
                dataForSubmit.vlasnikFL.mesto = values.ownerIndividualCity;
                dataForSubmit.vlasnikFL.brojPasosa = values.ownerIndividualPassportNumber;
            } else {
                dataForSubmit.vlasnikFL.mestoId = values.ownerIndividualCityInSerbia.id;
                dataForSubmit.vlasnikFL.mesto = values.ownerIndividualCityInSerbia.label;
                dataForSubmit.vlasnikFL.brojPasosa = null;
            }
        }

        if (values.ownerType === legalPersonType.legal) {
            dataForSubmit.vlasnikPL = {
                naziv: values.ownerLegalEntityName,
                drzavaId: constValues.countrySerbiaId,
                mestoId: values.ownerLegalEntityCityInSerbia.id,
                mesto: values.ownerLegalEntityCityInSerbia.label,
                adresa: values.ownerLegalEntityAddress.label
                    ? values.ownerLegalEntityAddress.label
                    : values.ownerLegalEntityAddress,
                adresaBroj: values.ownerLegalEntityAddressNumber,
                adresaStan: values.ownerLegalEntityAddressApartment,
                maticniBroj: values.ownerLegalEntityID,
                pib: values.ownerLegalEntityVAT,
                mobilniTelefon: formatPhoneValue(values.ownerLegalEntityPhone),
                email: values.ownerLegalEntityEmail,
            };
        }
    }

    dataForSubmit.vrstaObjektaID = values.objectType;
    dataForSubmit.objekatSifraProstora = values.objectCode;
    dataForSubmit.uzrokId = values.cause;
    if (values.isShowSMSLink)
        dataForSubmit.samostalnoSnimanjeStete = !values.objectHasSmsLink ? 'false' : values.objectHasSmsLink;
    dataForSubmit.vrsteOstecenja = values.damageTypes
        .filter((damageType: GenericObject) => damageType.checked === true)
        .map((damageType: GenericObject) => damageType.id);
    dataForSubmit.brojOstecenihProstorijaId = values.damagedRooms;
    dataForSubmit.opisOstecenja = values.damageDescription;

    let damagedRoomIds = [0];
    const showSpecification = values.damageTypes.filter(
        (damageType: GenericObject) => damageType.checked === true && damageType.shouldShowSpcification === true,
    );
    if (showSpecification.length > 0) {
        if (values.damagedRooms && values.damagedRooms === 1) damagedRoomIds = [1];
        else if (values.damagedRooms && values.damagedRooms === 2) damagedRoomIds = [1, 2];
        else if (values.damagedRooms && values.damagedRooms === 3) damagedRoomIds = [1, 2, 3];
        const damagedRoomsData: GenericObject[] = [];
        damagedRoomIds.map((damagedRoomId) => {
            if (values[`room${damagedRoomId}Area`] && values[`room${damagedRoomId}Area`] !== '') {
                const damagedRoomData: GenericObject = {
                    vrstaProstorijeId: values[`room${damagedRoomId}Type`],
                    povrsina: values[`room${damagedRoomId}Area`],
                };

                damagedRoomData.postojeOstecenjaNaPlafonu =
                    values[`room${damagedRoomId}IsDamageOnCeiling`] !== ''
                        ? values[`room${damagedRoomId}IsDamageOnCeiling`]
                        : null;
                damagedRoomData.brojZidovaProstorije =
                    values[`room${damagedRoomId}NumberOfDemagedWalls`] !== ''
                        ? values[`room${damagedRoomId}NumberOfDemagedWalls`]
                        : null;
                damagedRoomData.vrstaObradeZidovaID =
                    values[`room${damagedRoomId}WallFinishType`] !== ''
                        ? values[`room${damagedRoomId}WallFinishType`]
                        : null;
                damagedRoomData.napomena =
                    values[`room${damagedRoomId}Note`] !== '' ? values[`room${damagedRoomId}Note`] : null;

                damagedRoomsData.push(damagedRoomData);
            }
        });
        if (damagedRoomsData.length > 0) {
            dataForSubmit.specifikacijaOstecenja = damagedRoomsData;
        }
    }

    if (dataForSubmit.samostalnoSnimanjeStete === 'true') {
        switch (values.objectSmsLinkType) {
            case 1:
                dataForSubmit.samostalnoSnimanjeSteteTelefon = formatPhoneValue(values.submitterIndividualPhone);
                break;
            case 2:
                dataForSubmit.samostalnoSnimanjeSteteTelefon = formatPhoneValue(values.ownerIndividualPhone);
                break;
            case 3:
                dataForSubmit.samostalnoSnimanjeSteteTelefon = formatPhoneValue(
                    values.submitterLegalEntityContactPhone,
                );
                break;
            default:
                dataForSubmit.samostalnoSnimanjeSteteTelefon = values.objectSmsMobile;
        }
    }

    return dataForSubmit;
};
